// Shared Palworld TCG card price detail panel.
//
// One panel, used by BOTH the price chart (/prices) and the TCG Price Index
// (/tcg-price-index), so a card can never show two different stories depending
// on where it was clicked. It carries everything a buyer, seller or investor
// needs in one place: raw and graded asking markets, TCGplayer sold data, liquidity, the 30-day
// path with every turning point, the per-source quote audit, the live eBay
// auction board, marketplace and population-report links, and a hover
// explanation on every term that is not self-evident.
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowDownRight, ArrowUpRight, ExternalLink, Layers, X } from "lucide-react";
import {
  GRADING_SOURCES,
  MAX_QUOTE_AGE_DAYS,
  PRICE_SOURCES,
  SOURCE_BY_ID,
  isSoldSource,

  formatMoney,
  indexBreakdown,
  type CurrencyCode,
  type PriceRow,
} from "@/lib/prices-shared";
import { OFFICIAL_CARD_BY_CODE, RARITY_MEANINGS, SET_NAMES, isNotForSale, promoNote } from "@/lib/tcg-cards";
import { NotForSaleBadge } from "@/components/NotForSaleBadge";
import { RarityBadge } from "@/components/RarityBadge";
import { RatingStars } from "@/components/CardRating";
import { CardZoom } from "@/components/CardZoom";
import { InfoHint } from "@/components/InfoHint";
import { CardAuctionBoard } from "@/components/CardAuctionBoard";
import { AlertCreator } from "@/components/PriceAlertsPanel";
import { isFlatSeries } from "@/lib/chart-extrema";
import { HoverChart } from "@/components/HoverChart";
import { cardLiquidity } from "@/lib/liquidity";
import { cardLinkProps } from "@/lib/card-link";
import { trackAffiliateClick } from "@/lib/affiliate-clicks";
import { goUrl } from "@/lib/outbound";

export function Delta({ v }: { v: number }) {
  const up = v > 0;
  const flat = Math.abs(v) < 0.05;
  return (
    <span
      className={`inline-flex items-center gap-0.5 tabular-nums font-semibold ${
        flat ? "text-muted-foreground" : up ? "text-emerald-400" : "text-red-400"
      }`}
    >
      {!flat && (up ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />)}
      {flat ? "0.0%" : `${up ? "+" : ""}${v.toFixed(1)}%`}
    </span>
  );
}

/**
 * Full-size 30-day chart. The line stays clean — hover (or drag on touch) any
 * day to read that day's price, whether it came from a stored marketplace
 * snapshot or the Pal Stars Index baseline.
 */
// TCGplayer leads every single-card view: it is the only marketplace that
// gives Pal Stars validated completed sales for Palworld singles, so it is the
// structural authority for card values here. eBay stays as asking-price context.
const CHART_VIEWS = [
  { id: "tcgplayer", label: "TCGplayer" },
  { id: "ebay", label: "eBay" },
  { id: "blended", label: "Blended" },
] as const;

type ChartView = (typeof CHART_VIEWS)[number]["id"];

function PriceChart({ row, currency }: { row: PriceRow; currency: CurrencyCode }) {
  const [view, setView] = useState<ChartView>("tcgplayer");


  const points =
    view === "ebay" ? row.ebayHistory : view === "tcgplayer" ? row.tcgHistory : row.history;
  const soldAvg = view === "tcgplayer" ? row.tcgSoldAvg : view === "blended" ? row.soldAvg : null;
  const soldCount = view === "tcgplayer" ? row.tcgSoldCount : view === "blended" ? row.soldCount : 0;
  const recordedDays = points.filter((p) => p.real).length;
  const marketLabel = view === "ebay" ? "eBay" : view === "tcgplayer" ? "TCGplayer" : "Blended market";
  const soldPoints = view === "ebay" ? [] : row.tcgSoldHistory;
  const labels = [...new Set([...points.map((p) => p.day), ...soldPoints.map((p) => p.day)])].sort();
  const askByDay = new Map(points.map((p) => [p.day, p]));
  const soldByDay = new Map(soldPoints.map((p) => [p.day, p]));

  const vals = points.map((p) => p.value).filter((v) => Number.isFinite(v) && v > 0);
  const enough = points.length >= 2 && vals.length >= 2;
  const min = enough ? Math.min(...vals) : 0;
  const max = enough ? Math.max(...vals) : 0;
  const up = enough ? points[points.length - 1].value >= points[0].value : true;
  const flat = enough ? isFlatSeries(vals) : true;

  return (
    <figure className="mt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">
          {marketLabel} price history · {row.code}
          <InfoHint label="Price history">
            Each marketplace has its own asking-price line. eBay and TCGplayer are never substituted for one another.
            Blended combines validated English asking prices only.
          </InfoHint>
        </h3>
        <div className="flex flex-wrap gap-1.5">
          {CHART_VIEWS.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => setView(v.id)}
              aria-pressed={view === v.id}
              className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                view === v.id
                  ? "bg-primary/20 text-primary"
                  : "border border-border/60 text-muted-foreground hover:bg-card"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {!enough ? (
        <div className="mt-2 rounded-xl border border-dashed border-border/60 bg-card/30 p-4 text-xs text-muted-foreground">
          No {marketLabel === "Blended market" ? "price" : marketLabel} history for {row.code} yet
          {points.length === 1 ? " — only one day is recorded so far, so there is nothing to plot" : ""}. A line appears
          as soon as this printing has two days of validated {marketLabel === "Blended market" ? "market" : marketLabel}{" "}
          data. Use the marketplace links below in the meantime.
        </div>
      ) : (
        <>
          <HoverChart
            labels={labels}
            series={[
              {
                id: `${row.code}-${view}`,
                label: marketLabel,
                color: up ? "hsl(152 70% 50%)" : "hsl(0 75% 62%)",
                values: labels.map((day) => askByDay.get(day)?.value),
              },
              ...(soldPoints.length
                ? [{
                    id: `${row.code}-sold`,
                    label: "TCGplayer sold avg",
                    color: "hsl(42 92% 58%)",
                    values: labels.map((day) => soldByDay.get(day)?.value),
                  }]
                : []),
            ]}
            area
            baseline={view !== "ebay" && soldAvg && soldAvg > 0 ? soldAvg : undefined}
            format={(v) => formatMoney(v, currency)}
            ariaLabel={`${marketLabel} price history for ${row.name} ${row.code}`}
            columns={{
              label: "Listings sampled",
              values: labels.map((day) => askByDay.get(day)?.listings),
              color: "hsl(210 90% 66%)",
            }}
            tooltipExtra={(i) => (
              <div className="space-y-0.5 whitespace-nowrap text-[11px] text-muted-foreground">
                <p>{askByDay.get(labels[i])?.real ? `${marketLabel} snapshot` : "No asking snapshot"}</p>
                {view !== "ebay" ? (
                  <p>
                    TCGplayer sold avg:{" "}
                    {soldByDay.get(labels[i])
                      ? `${formatMoney(soldByDay.get(labels[i])!.value, currency)} (${soldByDay.get(labels[i])!.count} sale${soldByDay.get(labels[i])!.count === 1 ? "" : "s"})`
                      : "no completed TCGplayer sales yet"}
                  </p>
                ) : null}
              </div>
            )}
          />

          <figcaption className="mt-1 flex flex-wrap justify-between gap-x-3 text-[11px] text-muted-foreground">
            <span>
              {points.length} day{points.length === 1 ? "" : "s"} ago · {formatMoney(points[0].value, currency)}
            </span>
            <span>
              low {formatMoney(min, currency)} · high {formatMoney(max, currency)}
            </span>
            <span>today · {formatMoney(points[points.length - 1].value, currency)}</span>
            <span className="w-full">
              {flat
                ? `This printing has no recorded ${marketLabel} movement yet, so the line is flat — one price point until it trades.`
                : `Hover any day for that day's price. ${
                    recordedDays > 0
                      ? `${recordedDays} stored ${marketLabel} snapshot${recordedDays === 1 ? "" : "s"}. No other marketplace is substituted.`
                      : `No stored ${marketLabel} snapshots yet for this card.`
                  }`}
            </span>
          </figcaption>
        </>
      )}
    </figure>
  );
}

function SourceList({ row }: { row: PriceRow }) {
  const card = OFFICIAL_CARD_BY_CODE.get(row.code.toUpperCase());
  if (!card) return null;
  // Japanese / Chinese / Korean links are always offered as purchase options; they never
  // affect the English market value, price chart or price index.
  const sources = PRICE_SOURCES;
  return (
    <ul className="mt-3 grid gap-1.5 sm:grid-cols-2">
      {sources.map((s) => (

        <li key={s.id}>
          <a
            href={goUrl(s.id, "card", card.code)}
            onClick={() => trackAffiliateClick({ source: s.id, kind: "card", targetKey: row.code })}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-card/40 px-3 py-2 text-xs hover:border-primary/60 hover:bg-card"
            title={s.note}
          >
            <span className="min-w-0">
              <span className="block truncate font-semibold">{s.label}</span>
              <span className="text-[11px] text-muted-foreground">
                {s.region} · {s.kind === "sold" ? "sold data" : s.kind === "both" ? "asking + sold" : "asking"}
              </span>
            </span>
            <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          </a>
        </li>
      ))}
    </ul>
  );
}

/** Graded population report links for one card. */
function GradedPopList({ row }: { row: PriceRow }) {
  return (
    <div className="mt-4">
      <h4 className="text-sm font-bold">Graded population reports</h4>
      <p className="mt-1 text-[11px] text-muted-foreground">
        Check how many copies of {row.code} each grader has certified before paying a graded premium — a slab is only
        scarce if the pop at that grade is low.
      </p>
      <ul className="mt-2 grid gap-1.5 sm:grid-cols-3">
        {GRADING_SOURCES.map((g) => (
          <li key={g.id}>
            <a
              href={g.pop}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-card/40 px-3 py-2 text-xs hover:border-primary/60 hover:bg-card"
              title={g.note}
            >
              <span className="min-w-0">
                <span className="block truncate font-semibold">{g.company}</span>
                <span className="text-[11px] text-muted-foreground">{g.scale}</span>
              </span>
              <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

const CONFIDENCE_STYLE: Record<PriceRow["confidence"], string> = {
  modelled: "border-border/60 text-muted-foreground",
  low: "border-amber-500/50 text-amber-300",
  medium: "border-sky-500/50 text-sky-300",
  high: "border-emerald-500/50 text-emerald-300",
};

const CONFIDENCE_HINT: Record<PriceRow["confidence"], string> = {
  modelled: "No validated marketplace quote — Pal Stars Index baseline.",
  low: "One validated source and a thin sample.",
  medium: "Two or more sources, or a solid run of completed sales.",
  high: "Multiple sources plus five or more validated completed sales.",
};

/** "3d ago" style label for the newest completed sale behind a row. */
export function lastSoldLabel(row: PriceRow): string {
  if (!row.soldLastAt) return "—";
  const days = Math.max(0, Math.round((Date.now() - Date.parse(row.soldLastAt)) / 86400000));
  if (days === 0) return "today";
  if (days === 1) return "1d ago";
  if (days < 30) return `${days}d ago`;
  return `${Math.round(days / 30)}mo ago`;
}

export function ConfidenceBadge({ row }: { row: PriceRow }) {
  return (
    <span
      title={CONFIDENCE_HINT[row.confidence]}
      className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${CONFIDENCE_STYLE[row.confidence]}`}
    >
      {row.confidence}
    </span>
  );
}

/** Per-source quote audit: exactly what each marketplace reported, and its age. */
function QuoteAudit({ row, currency }: { row: PriceRow; currency: CurrencyCode }) {
  if (!row.quotes.length) {
    return (
      <p className="mt-2 text-xs text-muted-foreground">
        No stored marketplace quote yet. The daily sync validates every listing title, converts to USD and rejects
            outliers before a number lands here, so cards with no clean listings stay on the modelled index.
      </p>
    );
  }
  return (
    <div className="mt-2 min-w-0 overflow-hidden rounded-xl border border-border/60">
      <table className="w-full table-fixed text-xs">
        <caption className="sr-only">Validated marketplace quotes behind the blended market value</caption>
        <thead className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
          <tr className="border-b border-border/60">
            <th scope="col" className="w-[30%] px-2 py-1.5">Source</th>
            <th scope="col" className="px-2 py-1.5 text-right">Quote</th>
            <th scope="col" className="px-2 py-1.5 text-right">TCG sold</th>
            <th scope="col" className="hidden px-2 py-1.5 text-right sm:table-cell">Samples</th>
            <th scope="col" className="hidden px-2 py-1.5 text-right sm:table-cell">Age</th>
            <th scope="col" className="px-2 py-1.5">Used</th>
          </tr>
        </thead>
        <tbody>
          {row.quotes.map((q) => {
            const used = !isSoldSource(q.source) && !q.graded && q.trusted && q.ageDays <= MAX_QUOTE_AGE_DAYS && q.market !== null;
            return (
              <tr key={q.source} className="border-b border-border/40 last:border-0">
                <td className="truncate px-2 py-1.5">{SOURCE_BY_ID[q.source]?.label ?? q.source}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">
                  {q.market !== null ? formatMoney(q.market, currency) : "—"}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums">
                  {q.soldAvg !== null ? formatMoney(q.soldAvg, currency) : "—"}
                </td>
                <td className="hidden px-2 py-1.5 text-right tabular-nums sm:table-cell">{q.soldCount + q.listings}</td>
                <td className="hidden px-2 py-1.5 text-right tabular-nums sm:table-cell">{q.ageDays}d</td>
                <td className="px-2 py-1.5">
                  {used ? (
                    <span className="text-emerald-400">blended</span>
                  ) : (
                    <span className="text-muted-foreground">
                      {isSoldSource(q.source) ? "sold (separate)" : q.graded ? "graded (separate)" : !q.trusted ? "rejected" : "too old"}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Show-your-work panel for the modelled Pal Stars Index baseline. */
function IndexCalc({ row, currency }: { row: PriceRow; currency: CurrencyCode }) {
  const card = OFFICIAL_CARD_BY_CODE.get(row.code.toUpperCase());
  if (!card) return null;
  const { steps } = indexBreakdown(card);
  return (
    <details className="mt-2 rounded-xl border border-border/60 bg-card/40 p-3 text-xs">
      <summary className="cursor-pointer font-semibold">
        Show index calculation for {row.code} ({formatMoney(row.index, currency)})
      </summary>
      <ol className="mt-2 space-y-1.5">
        {steps.map((st) => (
          <li key={st.label} className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-semibold">{st.label}</span>
            {st.factor !== 1 ? <span className="text-muted-foreground">x {st.factor}</span> : null}
            <span className="ml-auto tabular-nums">{formatMoney(st.value, currency)}</span>
            <span className="w-full text-[11px] text-muted-foreground">{st.detail}</span>
          </li>
        ))}
      </ol>
      <p className="mt-2 text-[11px] text-muted-foreground">
        Baseline for {row.code}: <strong className="tabular-nums">{formatMoney(row.index, currency)}</strong>. The
        baseline is deterministic — no marketplace data in it — and is only used on days with no validated live quote.
        Today's market value of {formatMoney(row.market, currency)} is{" "}
        {row.index > 0 ? `${Math.round(((row.market - row.index) / row.index) * 100)}%` : "0%"} against it.
      </p>
    </details>
  );
}

/**
 * The complete card price panel.
 *
 * `onCompare` and `onAddAlert` are optional: the price chart passes them (it owns
 * the compare tray and the alert store), the price index opens the same panel
 * without them so the two pages stay in sync on everything that is data.
 */
export function CardPriceDetail({
  row,
  currency,
  onClose,
  onCompare,
  compared = false,
  onAddAlert,
}: {
  row: PriceRow;
  currency: CurrencyCode;
  onClose: () => void;
  onCompare?: (row: PriceRow) => void;
  compared?: boolean;
  onAddAlert?: Parameters<typeof AlertCreator>[0]["onAdd"];
}) {
  const liq = cardLiquidity(row);
  const gradedMultiple =
    row.gradedAvg && row.market > 0 ? Math.round((row.gradedAvg / row.market) * 10) / 10 : null;
  const spread = row.high > 0 ? Math.round(((row.high - row.low) / row.high) * 100) : 0;
  const vsIndex = row.index > 0 ? Math.round(((row.market - row.index) / row.index) * 100) : 0;

  const stats: { label: string; value: string; hint: string }[] = [
    { label: "Market", value: formatMoney(row.market, currency), hint: "Depth-weighted median of validated English asking listings. Completed sales are kept separate." },
    { label: "Lowest", value: formatMoney(row.low, currency), hint: "Cheapest validated raw copy seen in the latest sync." },
    { label: "Highest", value: formatMoney(row.high, currency), hint: "Priciest validated raw copy — one optimistic seller does not move Market." },
    {
      label: "Graded",
      value: row.gradedAvg ? formatMoney(row.gradedAvg, currency) : "—",
      hint: row.gradedAvg
        ? `Certified PSA / BGS / CGC slabs only, never blended into raw. ${row.gradedCount} slab listing(s)${row.gradedLow && row.gradedHigh ? `, ${formatMoney(row.gradedLow, currency)}–${formatMoney(row.gradedHigh, currency)}` : ""}.`
        : "No certified slab listed right now. Slabs are tracked separately from raw copies.",
    },
    {
      label: "TCGplayer sold avg",
      value: row.tcgSoldAvg ? formatMoney(row.tcgSoldAvg, currency) : "—",
      hint: `Completed TCGplayer sales only, across ${row.tcgSoldCount} validated sale(s). Never blended into asking prices.`,
    },

    { label: "Last sold", value: lastSoldLabel(row), hint: row.soldLastAt ? `Newest completed sale: ${new Date(row.soldLastAt).toUTCString()}.` : "No completed sale recorded yet for this printing." },
    { label: "Index", value: formatMoney(row.index, currency), hint: "Pal Stars Index baseline from printed rarity, set, chase status and playability. Used when no live quote exists." },
    { label: "Listings", value: String(row.listings), hint: "Validated active listings behind today's number. Fewer than three means treat the price as indicative." },
    { label: "Liquidity", value: `${liq.label} ${liq.score}`, hint: liq.detail },
    { label: "Ask spread", value: `${spread}%`, hint: "Gap between the cheapest and priciest listing. A wide spread means sellers disagree and there is room to negotiate." },
    { label: "vs index", value: `${vsIndex > 0 ? "+" : ""}${vsIndex}%`, hint: "How far the live market sits from the rarity baseline. Large positives are hype; large negatives can be a buying window." },
    { label: "Graded premium", value: gradedMultiple ? `${gradedMultiple}x raw` : "—", hint: "How many times the raw price a certified copy is asking. Compare against the population report before paying it." },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label={`${row.name} price detail`}
    >
      <button className="absolute inset-0 bg-background/80 backdrop-blur-sm" aria-label="Close price detail" onClick={onClose} />
       <div className="glass relative max-h-[92vh] w-[calc(100vw-0.5rem)] max-w-4xl overflow-x-hidden overflow-y-auto rounded-t-2xl border p-4 sm:w-[calc(100vw-2rem)] sm:rounded-2xl sm:p-5">
        <button
          onClick={onClose}
          className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-card"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="grid gap-5 sm:grid-cols-[minmax(0,260px)_1fr]">
          <div>
            <CardZoom
              src={row.image}
              alt={`${row.name} ${row.subtitle ? `“${row.subtitle}” ` : ""}Palworld TCG card ${row.code} (${row.rarity})`}
              caption={`${row.code} · ${row.name}`}
              cardCode={row.code}
              cardName={row.name}
            />
            <p className="mt-2 text-center text-[11px] text-muted-foreground">
              Scroll or pinch to zoom, drag to pan, double-click to reset.
            </p>
            {onCompare ? (
              <button
                onClick={() => onCompare(row)}
                className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-border/60 px-3 py-2 text-xs font-semibold hover:border-primary/60"
              >
                <Layers className="h-3.5 w-3.5" />
                {compared ? "Remove from compare" : "Add to compare"}
              </button>
            ) : null}
          </div>
          <div className="min-w-0">
            <h2 className="text-2xl font-bold leading-tight">{row.name}</h2>
            <p className="text-sm text-muted-foreground">
              {row.subtitle ? `${row.subtitle} · ` : ""}
              {row.code} · {SET_NAMES[row.set] ?? row.set}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <RarityBadge code={row.rarity} />
              <ConfidenceBadge row={row} />
              <span className="rounded-full border border-border/60 px-2 py-0.5 text-[11px]">{row.color}</span>
              <span className="rounded-full border border-border/60 px-2 py-0.5 text-[11px]">{row.type}</span>
              <span className="text-[11px] text-muted-foreground">{RARITY_MEANINGS[row.rarity]}</span>
              <RatingStars code={row.code} label={`${row.code} ${row.name}`} size="lg" />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {stats.map((s) => (
                <div key={s.label} className="rounded-xl border border-border/60 bg-card/40 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    {s.label}
                    <InfoHint label={s.label}>{s.hint}</InfoHint>
                  </p>
                  <p className="text-lg font-bold tabular-nums">{s.value}</p>
                </div>
              ))}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
              <span>
                7 days: <Delta v={row.change7d} />
              </span>
              <span>
                30 days: <Delta v={row.change30d} />
              </span>
              <span className="text-xs text-muted-foreground">
                {row.liveSources.length
                  ? `Live: ${row.liveSources.map((s) => SOURCE_BY_ID[s]?.label ?? s).join(", ")}`
                  : "Pal Stars Index baseline"}
              </span>
            </div>


            <PriceChart row={row} currency={currency} />

            <CardAuctionBoard code={row.code} currency={currency} />

            <h3 className="mt-5 text-sm font-bold uppercase tracking-wide text-muted-foreground">
              Where this number comes from
              <InfoHint label="Quote audit">
                Every marketplace quote stored for this card, whether it was blended into Market, and why any quote was
                rejected or ignored for being older than {MAX_QUOTE_AGE_DAYS} days.
              </InfoHint>
            </h3>
            <QuoteAudit row={row} currency={currency} />
            <IndexCalc row={row} currency={currency} />

            {onAddAlert ? <AlertCreator row={row} currency={currency} onAdd={onAddAlert} /> : null}

            <h3 className="mt-5 text-sm font-bold uppercase tracking-wide text-muted-foreground">
              Check every marketplace
            </h3>
            <SourceList row={row} />
            <GradedPopList row={row} />
            {(() => {
              const card = OFFICIAL_CARD_BY_CODE.get(row.code.toUpperCase());
              return card && isNotForSale(card) ? (
                <p className="mt-4 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <NotForSaleBadge note={promoNote(card)} />
                  <span>{promoNote(card)} It has no MSRP — every value here is secondary-market only.</span>
                </p>
              ) : null;
            })()}

            <div className="mt-4 flex flex-wrap gap-2 text-sm">
              <Link {...cardLinkProps(row.code)} className="rounded-lg border border-border/60 px-3 py-1.5 hover:border-primary/60">
                Open the {row.code} card page
              </Link>
              <Link to="/prices" className="rounded-lg border border-border/60 px-3 py-1.5 hover:border-primary/60">
                Full price chart
              </Link>
              <Link to="/tcg-price-index" className="rounded-lg border border-border/60 px-3 py-1.5 hover:border-primary/60">
                Compare against the TCG Price Index
              </Link>
              <Link to="/card-checker" search={{ q: row.code }} className="rounded-lg border border-border/60 px-3 py-1.5 hover:border-primary/60">
                Verify {row.code} in the Card Checker
              </Link>
              <Link to="/cards" className="rounded-lg border border-border/60 px-3 py-1.5 hover:border-primary/60">
                Full card database
              </Link>
              <Link
                to="/guides/$slug"
                params={{ slug: "palworld-card-prices-booster-box-demand" }}
                className="rounded-lg border border-border/60 px-3 py-1.5 hover:border-primary/60"
              >
                Price guide &amp; FAQ
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
